// common
import React from 'react';
// icon
import { LuRotateCcw } from 'react-icons/lu';

// style
import styled from 'styled-components';

// components
import Button from 'components/button/Button';

import useBilingTable from 'hooks/biling/useBilingTable';

import { formatThreeComma } from 'utils/number/formatNumberUtil';

import theme from 'styles/token/color';

/**
 * @component CalcFooter
 * @desc 요금 계산기 페이지 하단에 고정 표시되는 푸터 컴포넌트
 *
 * - 주월 예상 요금 합계를 실시간으로 표시합니다.
 * - 초기화 버튼 클릭 시 모든 슬라이더 값을 0으로 리셋합니다.
 * - '개별 문의'에 해당하는 항목이 있으면 원 대신 '개별 문의'를 표시합니다.
 */
export const CalcFooter = () => {
  const { values, handleResetClick } = useBilingTable();

  const total = React.useMemo(() => {
    if (!values || !values.cost) return '0';
    const sum = values.cost.reduce((acc, value) => {
      if (value === '개별 문의' || value === '문의하기') {
        return acc;
      }
      const numericValue = parseFloat(
        String(value)
          .replace(/[^0-9.]/g, '')
          .replace(/,/g, '')
      );
      return acc + (isNaN(numericValue) ? 0 : numericValue);
    }, 0);
    return formatThreeComma(Math.round(sum));
  }, [values.cost]);

  return (
    <CalcFooterStyle>
      <CalcFooterInner>
        {/* 서비스 예상 요금 레이블 */}
        <CalcBox>
          <CalcTitle>
            <strong>서비스 총 예상 요금</strong>
            <span>(1개월 기준, VAT 별도)</span>
          </CalcTitle>
        </CalcBox>

        {/* 초기화 버튼 + 합계 금액 표시 */}
        <CalcBox>
          <CalcResetButton
            beforeIcon={
              <LuRotateCcw
                style={{
                  width: '28px',
                  fontSize: '16px',
                  color: theme.color.white,
                }}
              />
            }
            onClick={handleResetClick}
          >
            초기화
          </CalcResetButton>
          <CalcResult>
            <CalcResultData>{total}</CalcResultData>
            원/월
          </CalcResult>
        </CalcBox>
      </CalcFooterInner>
    </CalcFooterStyle>
  );
};

// ─── Styled Components ─────────────────────────────────────────────────────────────────────────────────────────────
const CalcFooterStyle = styled.div`
  position: sticky;
  bottom: -1px;
  width: 100%;
  padding: 24px 32px;
  background: ${({ theme }) => theme.color.primaryDark};
  z-index: 50;
`;

const CalcFooterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  height: 100%;
  // max-width: 1280px;
  max-width: 1440px;
  margin: auto;
`;

const CalcBox = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;
const CalcTitle = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    color: ${({ theme }) => theme.color.white};
    font-size: 20px;
    font-weight: 500;
  }

  span {
    color: ${({ theme }) => theme.color.gray400};
    font-size: 13px;
  }
`;
const CalcResult = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.color.white};
`;
const CalcResetButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.color.white};
  font-size: 16px;
`;
const CalcResultData = styled.div`
  font-size: 32px;
  font-weight: 600;
  line-height: 1;
`;
